import { useMemo, useState } from "react"
import { Show, useObservable, useSelector } from "@legendapp/state/react"
import { View, ActivityIndicator, Pressable, Modal } from "react-native"
import { Link, router, useLocalSearchParams } from "expo-router"
import { useDebounce } from "use-debounce"
import { SafeAreaView } from "react-native-safe-area-context"

import { trpc, ENV, formatPrice, sharedStyles } from "@src/utils"
import { Header, InventoryCollectionPickModal, List, Text, TextInput, Image, MCIcon, Scanner } from "@src/components"
import { useNumCols } from "@src/hooks"
import { ar } from "@src/i18n/ar"

export default function StoragePlace() {
  const params = useLocalSearchParams<{ id: string }>()
  const STORAGE_PLACE_ID = Number.parseInt(params.id, 10)
  const [search, setSearch] = useState("")
  const [showScanner, setShowScanner] = useState(false)
  const [debouncedSearch] = useDebounce(search, 500)
  const state$ = useObservable({ search: false, sortByLatestUpdate: false, collectionsModal: false })
  const numCols = Math.max(2, useNumCols(250, { maxWidth: 1400 }))

  const [collectionIds, setCollectionIds] = useState<number[]>([])
  const sortByLatestUpdate = useSelector(() => state$.sortByLatestUpdate.get())

  const products = trpc.storagePlaces.listProducts.useInfiniteQuery(
    { search: debouncedSearch, storagePlaceId: STORAGE_PLACE_ID, sortByLatestUpdate, inventoryCollectionIds: collectionIds.length > 0 ? collectionIds : undefined },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )

  const allProducts = useMemo(() => products.data?.pages.flatMap((page) => page.items) ?? [], [products.data])

  const isFilterActive = collectionIds.length > 0

  function handleBarcodeSubmit(id: number) {
    router.push(`/dashboard/inventory/storage/${STORAGE_PLACE_ID}/product/${id}`)
  }

  return (
    <>
      <Header
        title={ar.mainStoragePlace}
        right={
          <View className="flex-row h-full">
            <Show if={() => state$.search.get() !== true}>
              <Pressable className="px-2 h-full justify-center" onPress={() => state$.search.set(true)}>
                <MCIcon name="magnify" size={27} />
              </Pressable>
            </Show>
            <Pressable onPress={() => setShowScanner(true)} className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="barcode" size={24} />
            </Pressable>
            <Pressable className="px-2 h-full justify-center" onPress={() => state$.collectionsModal.set(true)}>
              <MCIcon name="filter-variant" size={27} color={isFilterActive ? "#3B82F6" : undefined} />
              {isFilterActive && <View className="absolute top-2 right-1 w-2 h-2 rounded-full bg-blue-500" />}
            </Pressable>
            <Pressable className="px-2 h-full justify-center" onPress={() => state$.sortByLatestUpdate.set(!state$.sortByLatestUpdate.get())}>
              <MCIcon name="clock-outline" size={27} color={sortByLatestUpdate ? "#3B82F6" : undefined} />
            </Pressable>
            <Link asChild href={`/dashboard/inventory/storage/${STORAGE_PLACE_ID}/normalize`}>
              <Pressable className="px-2 h-full justify-center active:opacity-70">
                <MCIcon name="auto-fix" size={24} color="#F97316" />
              </Pressable>
            </Link>
            <Pressable className="px-2 h-full justify-center" onPress={() => products.refetch()}>
              <MCIcon name="refresh" size={27} />
            </Pressable>
          </View>
        }
      />

      <Show if={state$.search}>
        <View className="max-w-content-wide mx-auto w-full">
          <TextInput value={search} onChangeText={setSearch} autoFocus placeholder={ar.search} />
          <Pressable
            className="absolute top-0 bottom-0 end-0 px-3 justify-center"
            onPress={() => {
              state$.search.set(false)
              setSearch("")
            }}
          >
            <MCIcon name="close" size={25} />
          </Pressable>
        </View>
      </Show>

      <List
        contentContainerStyle={sharedStyles.contentContainerWide}
        data={allProducts}
        numColumns={numCols}
        key={`storage-products-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        onEndReached={() => products.hasNextPage && products.fetchNextPage()}
        onRefresh={products.refetch}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={
          products.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">No products found in this storage place.</Text>
        }
        renderItem={({ item }) => {
          const isWeightProduct = item.type === "WEIGHT_PRODUCT"
          const quantity = item.quantity
          const image = item.images?.at(-1)

          // Low stock check
          const isLowStock = (item.lowStockThreshold && item.quantity < item.lowStockThreshold) || item.quantity <= 0

          return (
            <View
              className={`bg-white p-3 rounded mb-3 shadow-sm mx-1 flex-1 hover:shadow-xl transition-all ${
                isLowStock ? "border-2 border-red-500 shadow-lg shadow-red-200 bg-red-50/30" : "border border-gray-100"
              }`}
            >
              <Link href={`/dashboard/inventory/storage/${STORAGE_PLACE_ID}/product/${item.id}`} asChild>
                <Pressable className="active:opacity-70">
                  <View className={`absolute top-0 right-0 z-10 ${BADGE_BG[item.type]} rounded-sm px-2 py-1 shadow-md`}>
                    <Text className="text-white text-xs font-bold">{isWeightProduct ? `${(quantity / 1000).toFixed(2)} ${ar.kg}` : `×${quantity}`}</Text>
                  </View>

                  {image ? (
                    <Image
                      contentFit="contain"
                      cachePolicy="memory"
                      source={`${ENV.R2_URL}/${image.key}`}
                      style={{ width: "100%", height: 120 }}
                      recyclingKey={image.key}
                      transition={250}
                      loading="lazy"
                    />
                  ) : (
                    <View className="w-full h-28 bg-gray-100 rounded items-center justify-center">
                      <MCIcon name="package-variant" size={40} color="#9CA3AF" />
                    </View>
                  )}

                  <View className="mt-3">
                    <Text className="text-sm font-semibold text-gray-900 text-center leading-tight" numberOfLines={2} style={{ minHeight: 36 }}>
                      {item.name}
                    </Text>

                    <View className="mt-3 items-center">
                      <Text className="text-base md:text-lg font-bold text-green-600">{formatPrice(item.price)}</Text>
                      {item.upc && <Text className="text-xs text-gray-500 mt-1">{item.upc}</Text>}
                    </View>
                  </View>
                </Pressable>
              </Link>
            </View>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />

      <Show if={state$.collectionsModal}>
        <InventoryCollectionPickModal
          value={collectionIds}
          onChange={(ids) => {
            setCollectionIds(ids)
            state$.collectionsModal.set(false)
          }}
          hide={() => state$.collectionsModal.set(false)}
        />
      </Show>

      <Modal visible={showScanner} animationType="slide" onRequestClose={() => setShowScanner(false)}>
        <Scanner onSubmit={handleBarcodeSubmit} hide={() => setShowScanner(false)} />
      </Modal>
    </>
  )
}

const BADGE_BG = {
  LOCAL_PRODUCT: "bg-blue-800",
  STANDARD_PRODUCT: "bg-emerald-800",
  WEIGHT_PRODUCT: "bg-rose-800",
  WEIGHT_DEFINED_PRODUCT: "bg-amber-800",
}
